/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Plus, X, CheckCircle, Circle, Edit2, Trash2, BookOpen, Users, Filter, ChevronRight, AlertCircle } from 'lucide-react';

// ── Colour system ─────────────────────────────────────────────────────
const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
};

// ── Constants ─────────────────────────────────────────────────────────
const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const DEPTS  = ['FOH', 'KIT', 'Management'];

// ── Shared styles ─────────────────────────────────────────────────────
const pill = (active, small = false) => ({
  padding:      small ? '4px 10px' : '6px 14px',
  borderRadius: '20px',
  border:       `1px solid ${active ? C.navy : C.border}`,
  cursor:       'pointer',
  fontSize:     small ? '12px' : '13px',
  fontWeight:   active ? 600 : 400,
  background:   active ? C.navy : C.white,
  color:        active ? C.white : C.mid,
  transition:   'all 0.15s',
});

const inputStyle = {
  width: '100%', padding: '8px 10px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '13px', color: C.charcoal, background: C.white,
  outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  fontSize: '12px', fontWeight: 600, color: C.charcoal,
  display: 'block', marginBottom: '4px',
};
const textareaStyle = { ...inputStyle, resize: 'vertical', fontFamily: 'inherit' };

// ── ArrayEditor (manager form helper) ────────────────────────────────
function ArrayEditor({ label, value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange([...value, draft.trim()]);
    setDraft('');
  };
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '6px' }}>
        {value.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: C.cream, borderRadius: '6px', border: `1px solid ${C.border}` }}>
            <span style={{ flex: 1, fontSize: '13px', color: C.charcoal }}>{item}</span>
            <button onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: 0 }}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={placeholder} style={{ ...inputStyle, flex: 1 }} />
        <button onClick={add} style={{ padding: '8px 14px', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          Add
        </button>
      </div>
    </div>
  );
}

// ── Completion progress bar ───────────────────────────────────────────
function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = pct === 100 ? C.green : pct > 50 ? C.amber : C.red;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ flex: 1, height: '6px', background: C.border, borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: '3px', transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal, whiteSpace: 'nowrap' }}>
        {done}/{total}
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// STAFF VIEW — card list, filtered to dept, click for detail + who's pending
// ══════════════════════════════════════════════════════════════════════
function StaffTrainingView({ currentUser, trainings, members, completions }) {
  const [detailTraining, setDetailTraining] = useState(null);

  // Trainings relevant to this staff login's dept + venue
  const myDept  = currentUser.role === 'bar' ? 'FOH' : 'KIT';
  const myVenue = currentUser.venue;

  const relevantTrainings = trainings.filter(t => {
    const deptMatch  = t.dept === 'All' || t.dept === myDept;
    const venueMatch = t.venue === 'All' || t.venue === myVenue;
    return deptMatch && venueMatch;
  });

  // Members at this venue + dept (for completion tracking)
  const venueMembers = members.filter(m => m.venue === myVenue && m.dept === myDept);

  const getCompletion = (trainingId) => {
    const done    = venueMembers.filter(m => completions.some(c => c.training_id === trainingId && c.team_member_id === m.id));
    const pending = venueMembers.filter(m => !completions.some(c => c.training_id === trainingId && c.team_member_id === m.id));
    return { done, pending, total: venueMembers.length };
  };

  const required   = relevantTrainings.filter(t => t.required);
  const optional   = relevantTrainings.filter(t => !t.required);

  function TrainingCard({ t }) {
    const { done, pending, total } = getCompletion(t.id);
    const allDone   = total > 0 && pending.length === 0;
    const noneDone  = done.length === 0;
    const statusColor = allDone ? C.green : noneDone ? C.red : C.amber;
    const statusBg    = allDone ? C.greenLight : noneDone ? C.redLight : C.amberLight;

    return (
      <div onClick={() => setDetailTraining(t)}
        style={{
          background: C.white, borderRadius: '12px',
          border: `1px solid ${C.border}`,
          borderLeft: `4px solid ${statusColor}`,
          padding: '16px 18px', cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          display: 'flex', alignItems: 'center', gap: '14px',
          transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.1)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'}
      >
        {/* Status icon */}
        <div style={{ flexShrink: 0 }}>
          {allDone
            ? <CheckCircle size={24} color={C.green} fill={C.greenLight} />
            : <AlertCircle size={24} color={statusColor} />
          }
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '14px', color: C.charcoal }}>{t.title}</span>
            {t.required && (
              <span style={{ padding: '2px 8px', borderRadius: '10px', background: C.redLight, color: C.red, fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>
                REQUIRED
              </span>
            )}
          </div>
          {t.description && (
            <p style={{ margin: '0 0 8px', fontSize: '12px', color: C.mid, lineHeight: 1.5,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.description}
            </p>
          )}
          {total > 0 && <ProgressBar done={done.length} total={total} />}
          {total === 0 && (
            <span style={{ fontSize: '11px', color: C.mid }}>No staff assigned to this venue/dept yet</span>
          )}
        </div>

        {/* Pending badge + arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {pending.length > 0 && (
            <span style={{ padding: '3px 10px', borderRadius: '20px', background: statusBg, color: statusColor, fontSize: '11px', fontWeight: 700 }}>
              {pending.length} pending
            </span>
          )}
          <ChevronRight size={16} color={C.mid} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 700, color: C.charcoal }}>Training</h2>
        <p style={{ margin: 0, fontSize: '13px', color: C.mid }}>
          {myVenue} · {myDept === 'FOH' ? 'Front of House' : 'Kitchen'} ·{' '}
          {relevantTrainings.length} training{relevantTrainings.length !== 1 ? 's' : ''}
        </p>
      </div>

      {relevantTrainings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.mid, background: C.white, borderRadius: '12px', border: `1px solid ${C.border}` }}>
          No trainings assigned yet
        </div>
      ) : (
        <>
          {required.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                Required
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {required.map(t => <TrainingCard key={t.id} t={t} />)}
              </div>
            </div>
          )}
          {optional.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                Additional Training
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {optional.map(t => <TrainingCard key={t.id} t={t} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {detailTraining && (() => {
        const { done, pending, total } = getCompletion(detailTraining.id);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
            onClick={() => setDetailTraining(null)}>
            <div style={{ background: C.white, borderRadius: '16px', width: '600px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
              onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: C.white, zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: C.charcoal }}>{detailTraining.title}</h2>
                      {detailTraining.required && (
                        <span style={{ padding: '2px 8px', borderRadius: '10px', background: C.redLight, color: C.red, fontSize: '10px', fontWeight: 700 }}>REQUIRED</span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: C.mid }}>
                      {detailTraining.dept !== 'All' ? detailTraining.dept : 'All Departments'} · {detailTraining.venue !== 'All' ? detailTraining.venue : 'All Venues'}
                    </div>
                  </div>
                  <button onClick={() => setDetailTraining(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}>
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Description */}
                {detailTraining.description && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <BookOpen size={13} /> Overview
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: C.charcoal, lineHeight: 1.6 }}>{detailTraining.description}</p>
                  </div>
                )}

                {/* Competencies */}
                {detailTraining.competencies?.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Competencies</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {detailTraining.competencies.map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: C.charcoal }}>
                          <span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>✓</span> {c}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Takeaways */}
                {detailTraining.takeaways?.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Key Takeaways</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {detailTraining.takeaways.map((t, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: C.charcoal }}>
                          <span style={{ color: C.orange, fontWeight: 700, flexShrink: 0 }}>→</span> {t}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Steps */}
                {detailTraining.steps?.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Step-by-Step</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {detailTraining.steps.map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: C.navy, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                          <p style={{ margin: 0, fontSize: '13px', color: C.charcoal, lineHeight: 1.5, paddingTop: '4px' }}>{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completion — who's pending */}
                {total > 0 && (
                  <div style={{ background: C.cream, borderRadius: '10px', padding: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Users size={13} /> Team Completion
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <ProgressBar done={done.length} total={total} />
                    </div>
                    {pending.length > 0 ? (
                      <div>
                        <div style={{ fontSize: '11px', color: C.mid, marginBottom: '8px' }}>Still to complete:</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {pending.map(m => (
                            <span key={m.id} style={{ padding: '4px 12px', borderRadius: '20px', background: C.redLight, color: C.red, fontSize: '12px', fontWeight: 500 }}>
                              {m.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: C.green, fontSize: '13px', fontWeight: 600 }}>
                        <CheckCircle size={16} /> All team members completed
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MANAGER VIEW — full matrix with filters, add/edit/delete
// ══════════════════════════════════════════════════════════════════════
function ManagerTrainingView({ currentUser, trainings, setTrainings, members, completions, setCompletions }) {
  const [venueFilter, setVenueFilter] = useState('All');
  const [deptFilter,  setDeptFilter]  = useState('All');
  const [showForm,    setShowForm]    = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [detailTraining, setDetailTraining] = useState(null);
  const [deleteConfirm,  setDeleteConfirm]  = useState(null);

  const emptyForm = { title: '', dept: 'All', venue: 'All', description: '', competencies: [], takeaways: [], steps: [], required: false };
  const [form, setForm] = useState(emptyForm);

  const isAdmin = currentUser?.role === 'admin';

  const filteredTrainings = trainings.filter(t => {
    const venueMatch = venueFilter === 'All' || t.venue === 'All' || t.venue === venueFilter;
    const deptMatch  = deptFilter  === 'All' || t.dept  === 'All' || t.dept  === deptFilter;
    return venueMatch && deptMatch;
  });

  const filteredMembers = members.filter(m => {
    const venueMatch = venueFilter === 'All' || m.venue === venueFilter;
    const deptMatch  = deptFilter  === 'All' || m.dept  === deptFilter;
    return venueMatch && deptMatch;
  });

  const isComplete = (trainingId, memberId) =>
    completions.some(c => c.training_id === trainingId && c.team_member_id === memberId);

  const handleToggleCompletion = async (trainingId, memberId) => {
    const existing = completions.find(c => c.training_id === trainingId && c.team_member_id === memberId);
    if (existing) {
      await supabase.from('training_completions').delete().eq('id', existing.id);
      setCompletions(prev => prev.filter(c => c.id !== existing.id));
    } else {
      const { data } = await supabase.from('training_completions').insert([{
        training_id:    trainingId,
        team_member_id: memberId,
        completed_by:   currentUser?.email || currentUser?.name || 'unknown',
      }]).select().single();
      if (data) setCompletions(prev => [...prev, data]);
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setForm({ title: t.title, dept: t.dept || 'All', venue: t.venue || 'All', description: t.description || '', competencies: t.competencies || [], takeaways: t.takeaways || [], steps: t.steps || [], required: t.required || false });
    setEditingId(t.id);
    setShowForm(true);
    setDetailTraining(null);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = { title: form.title.trim(), dept: form.dept, venue: form.venue, description: form.description.trim(), competencies: form.competencies, takeaways: form.takeaways, steps: form.steps, required: form.required };
    if (editingId) {
      const { data } = await supabase.from('trainings').update(payload).eq('id', editingId).select().single();
      if (data) setTrainings(prev => prev.map(t => t.id === editingId ? data : t));
    } else {
      const { data } = await supabase.from('trainings').insert([payload]).select().single();
      if (data) setTrainings(prev => [...prev, data].sort((a, b) => a.title.localeCompare(b.title)));
    }
    setSaving(false);
    setShowForm(false);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    await supabase.from('trainings').delete().eq('id', id);
    setTrainings(prev => prev.filter(t => t.id !== id));
    setDeleteConfirm(null);
    setDetailTraining(null);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', fontFamily: 'inherit' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: C.charcoal }}>Training</h2>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: C.mid }}>
            {filteredTrainings.length} training{filteredTrainings.length !== 1 ? 's' : ''} · {filteredMembers.length} team members
          </p>
        </div>
        <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
          <Plus size={15} /> Add Training
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>
        <Filter size={14} color={C.mid} />
        {['All', ...VENUES].map(v => (
          <button key={v} onClick={() => setVenueFilter(v)} style={pill(venueFilter === v, true)}>{v}</button>
        ))}
        <div style={{ width: '1px', background: C.border, margin: '0 4px', height: '20px' }} />
        {['All', ...DEPTS].map(d => (
          <button key={d} onClick={() => setDeptFilter(d)} style={pill(deptFilter === d, true)}>{d}</button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: C.charcoal }}>{editingId ? 'Edit Training' : 'New Training'}</h3>
            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={20} /></button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Training Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Order of Service — Box Jordaan" style={inputStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Department</label>
                <select value={form.dept} onChange={e => setForm(f => ({ ...f, dept: e.target.value }))} style={inputStyle}>
                  <option value="All">All Departments</option>
                  {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Venue</label>
                <select value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} style={inputStyle}>
                  <option value="All">All Venues</option>
                  {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: C.charcoal }}>
                  <input type="checkbox" checked={form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked }))} />
                  Required / Mandatory
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Overview / Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="What this training covers and why it matters..." style={textareaStyle} />
            </div>
            <ArrayEditor label="Competencies" value={form.competencies} onChange={v => setForm(f => ({ ...f, competencies: v }))} placeholder="e.g. Can take a full table order independently" />
            <ArrayEditor label="Key Takeaways" value={form.takeaways} onChange={v => setForm(f => ({ ...f, takeaways: v }))} placeholder="e.g. Understands allergen policy" />
            <ArrayEditor label="Step-by-Step Procedure" value={form.steps} onChange={v => setForm(f => ({ ...f, steps: v }))} placeholder="e.g. Greet guests within 30 seconds of seating" />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button onClick={handleSave} disabled={saving || !form.title.trim()}
              style={{ ...pill(true), opacity: saving || !form.title.trim() ? 0.6 : 1, cursor: saving || !form.title.trim() ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving...' : editingId ? 'Update Training' : 'Save Training'}
            </button>
            <button onClick={() => setShowForm(false)} style={pill(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Matrix */}
      {filteredTrainings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.mid, background: C.white, borderRadius: '12px', border: `1px solid ${C.border}` }}>
          No trainings found — click Add Training to get started
        </div>
      ) : filteredMembers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.mid, background: C.white, borderRadius: '12px', border: `1px solid ${C.border}` }}>
          No team members match this filter
        </div>
      ) : (
        <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', background: C.cream, position: 'sticky', left: 0, zIndex: 2, minWidth: '180px', borderRight: `1px solid ${C.border}` }}>
                  Team Member
                </th>
                {filteredTrainings.map(t => {
                  const applicable = filteredMembers.filter(m => t.dept === 'All' || m.dept === t.dept);
                  const done = applicable.filter(m => isComplete(t.id, m.id));
                  const pct  = applicable.length > 0 ? Math.round((done.length / applicable.length) * 100) : 0;
                  const barColor = pct === 100 ? C.green : pct > 50 ? C.amber : C.red;
                  return (
                    <th key={t.id} style={{ padding: '12px 14px', textAlign: 'center', background: C.cream, minWidth: '130px', borderRight: `1px solid ${C.border}`, verticalAlign: 'bottom' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <button onClick={() => setDetailTraining(t)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '12px', color: C.charcoal, textAlign: 'center', lineHeight: 1.3, padding: 0 }}>
                          {t.title}
                          {t.required && <span style={{ display: 'block', fontSize: '10px', color: C.red, fontWeight: 700 }}>REQUIRED</span>}
                        </button>
                        <div style={{ fontSize: '11px', color: C.mid }}>{done.length}/{applicable.length}</div>
                        <div style={{ width: '80px', height: '4px', background: C.border, borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: pct + '%', background: barColor, borderRadius: '2px', transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member, idx) => (
                <tr key={member.id} style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? C.white : C.cream }}>
                  <td style={{ padding: '12px 20px', position: 'sticky', left: 0, background: idx % 2 === 0 ? C.white : C.cream, zIndex: 1, borderRight: `1px solid ${C.border}` }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: C.charcoal }}>{member.name}</div>
                    <div style={{ fontSize: '11px', color: C.mid }}>{member.dept} · {member.venue}</div>
                  </td>
                  {filteredTrainings.map(t => {
                    const applicable = t.dept === 'All' || t.dept === member.dept;
                    const done = isComplete(t.id, member.id);
                    return (
                      <td key={t.id} style={{ textAlign: 'center', padding: '12px 14px', borderRight: `1px solid ${C.border}` }}>
                        {applicable ? (
                          <button onClick={() => handleToggleCompletion(t.id, member.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            title={done ? 'Mark incomplete' : 'Mark complete'}>
                            {done
                              ? <CheckCircle size={20} color={C.green} fill={C.greenLight} />
                              : <Circle size={20} color={C.border} />
                            }
                          </button>
                        ) : (
                          <span style={{ color: C.border, fontSize: '16px' }}>—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Training Detail Modal */}
      {detailTraining && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          onClick={() => setDetailTraining(null)}>
          <div style={{ background: C.white, borderRadius: '16px', width: '640px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: C.white, zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: C.charcoal }}>{detailTraining.title}</h2>
                    {detailTraining.required && <span style={{ padding: '2px 8px', borderRadius: '10px', background: C.redLight, color: C.red, fontSize: '10px', fontWeight: 700 }}>REQUIRED</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: C.mid }}>
                    {detailTraining.dept !== 'All' ? detailTraining.dept : 'All Departments'} · {detailTraining.venue !== 'All' ? detailTraining.venue : 'All Venues'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button onClick={() => openEdit(detailTraining)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: C.cream, border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: C.charcoal, fontWeight: 500 }}>
                    <Edit2 size={12} /> Edit
                  </button>
                  {isAdmin && (
                    <button onClick={() => setDeleteConfirm(detailTraining)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: C.redLight, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: C.red, fontWeight: 500 }}>
                      <Trash2 size={12} /> Delete
                    </button>
                  )}
                  <button onClick={() => setDetailTraining(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={20} /></button>
                </div>
              </div>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {detailTraining.description && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <BookOpen size={13} /> Overview
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: C.charcoal, lineHeight: 1.6 }}>{detailTraining.description}</p>
                </div>
              )}
              {detailTraining.competencies?.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Competencies</div>
                  {detailTraining.competencies.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', color: C.charcoal, marginBottom: '4px' }}>
                      <span style={{ color: C.green, fontWeight: 700 }}>✓</span> {c}
                    </div>
                  ))}
                </div>
              )}
              {detailTraining.takeaways?.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Key Takeaways</div>
                  {detailTraining.takeaways.map((t, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', color: C.charcoal, marginBottom: '4px' }}>
                      <span style={{ color: C.orange, fontWeight: 700 }}>→</span> {t}
                    </div>
                  ))}
                </div>
              )}
              {detailTraining.steps?.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Step-by-Step</div>
                  {detailTraining.steps.map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: C.navy, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                      <p style={{ margin: 0, fontSize: '13px', color: C.charcoal, lineHeight: 1.5, paddingTop: '4px' }}>{s}</p>
                    </div>
                  ))}
                </div>
              )}
              {/* Completion summary */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={13} /> Team Completion
                </div>
                {(() => {
                  const relevant = members.filter(m => detailTraining.dept === 'All' || m.dept === detailTraining.dept);
                  const done     = relevant.filter(m => isComplete(detailTraining.id, m.id));
                  const notDone  = relevant.filter(m => !isComplete(detailTraining.id, m.id));
                  const pct      = relevant.length > 0 ? Math.round((done.length / relevant.length) * 100) : 0;
                  return (
                    <div>
                      <div style={{ marginBottom: '10px' }}><ProgressBar done={done.length} total={relevant.length} /></div>
                      {notDone.length > 0 && (
                        <div>
                          <div style={{ fontSize: '11px', color: C.mid, marginBottom: '6px' }}>Still to complete:</div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {notDone.map(m => (
                              <span key={m.id} style={{ padding: '3px 10px', borderRadius: '20px', background: C.redLight, color: C.red, fontSize: '11px', fontWeight: 500 }}>{m.name}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDeleteConfirm(null)}>
          <div style={{ background: C.white, borderRadius: '14px', padding: '24px', width: '360px', maxWidth: '95%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.redLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Trash2 size={16} color={C.red} />
              </div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: C.charcoal }}>Delete Training</h3>
            </div>
            <p style={{ fontSize: '13px', color: C.mid, margin: '0 0 20px' }}>
              Delete <strong>{deleteConfirm.title}</strong>? All completion records will also be removed.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setDeleteConfirm(null)} style={pill(false)}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)}
                style={{ padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: C.red, color: C.white }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ROOT — loads data, routes to correct view based on role
// ══════════════════════════════════════════════════════════════════════
export default function Training({ currentUser }) {
  const [trainings,   setTrainings]   = useState([]);
  const [members,     setMembers]     = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading,     setLoading]     = useState(true);

  const isStaff = currentUser?.role === 'bar' || currentUser?.role === 'kitchen';

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: tData }, { data: mData }, { data: cData }] = await Promise.all([
      supabase.from('trainings').select('*').order('title'),
      supabase.from('team_members').select('id, name, venue, dept').order('name'),
      supabase.from('training_completions').select('*'),
    ]);
    if (tData) setTrainings(tData);
    if (mData) setMembers(mData || []);
    if (cData) setCompletions(cData || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px', color: C.mid }}>
        Loading...
      </div>
    );
  }

  // Staff get the simplified card view
  if (isStaff) {
    return (
      <StaffTrainingView
        currentUser={currentUser}
        trainings={trainings}
        members={members}
        completions={completions}
      />
    );
  }

  // Manager / Admin get the full matrix
  return (
    <ManagerTrainingView
      currentUser={currentUser}
      trainings={trainings}
      setTrainings={setTrainings}
      members={members}
      completions={completions}
      setCompletions={setCompletions}
    />
  );
}